import {
    AlertModal,
    Box,
    Button,
    DeleteModal,
    Modal,
    showToast,
    SubPageLayout,
    TextBlock,
} from "@bcc-ui/ui-components";
import { t } from "i18next";
import React from "react";
import { useSelector } from "react-redux";

import style from "./single-sign-on-settings-subpage.scss";
import { SingleSignOnDetails } from "components/settings/views/single-sign-on-settings/single-sign-on-details/SingleSignOnDetails";
import {
    CREATE_SSO_CONFIGURATION_FORM_ID,
    SingleSignOnSettingsForm,
    SsoFormValues,
} from "components/settings/views/single-sign-on-settings/single-sign-on-settings-form/SingleSignOnSettingsForm";
import { SsoConfiguration, SsoProtocol } from "domain/users";
import { userService } from "services/user/users/UserService";
import { StoreState } from "store";

import testIds from "testIds.json";

type SsoState =
    | { isConfigured: true; ssoLoginDetails: SsoConfiguration }
    | { isConfigured: false; ssoLoginDetailsByProtocol: Record<SsoProtocol, SsoConfiguration> };

const is404Error = (error: unknown) => error instanceof Error && "status" in error && error.status === 404;

export const SSO_LOGIN_URL_POLLING_INTERVAL_MILLISECONDS = 10_000;
export const SSO_LOGIN_URL_MAX_POLLING_RETRIES = 5;

export const SingleSignOnSettingsSubpage = () => {
    const tenantDetails = useSelector(
        (state: StoreState) => state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1]
    );

    const [isLoading, setIsLoading] = React.useState(true);
    const [alertModalState, setAlertModalState] = React.useState({
        title: t("Settings.errorTitle"),
        message: t("Common.failureMessage"),
        isVisible: false,
    });
    const [isConfirmDeleteModalVisible, setIsConfirmDeleteModalVisible] = React.useState(false);
    const [isCancelModalVisible, setIsCancelModalVisible] = React.useState(false);
    const [formResetCount, setFormResetCount] = React.useState(0);
    const [ssoState, setSsoState] = React.useState<SsoState>();
    const [isPreviousConfigurationDeleted, setIsPreviousConfigurationDeleted] = React.useState(false);
    const [isDirty, setIsDirty] = React.useState(false);
    const [isValid, setIsValid] = React.useState(false);
    const [submitCount, setSubmitCount] = React.useState(0);
    const abortControllerRef = React.useRef<AbortController>();

    React.useEffect(() => () => abortControllerRef.current?.abort(), []);

    const pollSsoLoginUrl = async (
        tenantUuid: string,
        protocol: string,
        abortController: AbortController
    ): Promise<SsoConfiguration | undefined> => {
        let retryCount = 0;
        while (retryCount <= SSO_LOGIN_URL_MAX_POLLING_RETRIES) {
            if (abortController.signal.aborted) {
                return undefined;
            }
            const ssoConfiguration = await userService.fetchSsoSettings(tenantUuid, protocol, abortController);
            if (ssoConfiguration.loginUrl) {
                return ssoConfiguration;
            }
            retryCount++;
            if (retryCount <= SSO_LOGIN_URL_MAX_POLLING_RETRIES) {
                await new Promise((resolve) => setTimeout(resolve, SSO_LOGIN_URL_POLLING_INTERVAL_MILLISECONDS));
            }
        }
        return undefined;
    };

    const fetchUnconfiguredSsoDetails = async (tenantUuid: string, abortController: AbortController) => {
        const [saml, oidc] = await Promise.all([
            userService.fetchSsoSettings(tenantUuid, SsoProtocol.SAML, abortController),
            userService.fetchSsoSettings(tenantUuid, SsoProtocol.OIDC, abortController),
        ]);
        return { [SsoProtocol.SAML]: saml, [SsoProtocol.OIDC]: oidc };
    };

    const fetchSsoConfiguration = async (
        tenantUuid: string,
        abortController: AbortController
    ): Promise<SsoState | undefined> => {
        try {
            const ssoLoginDetails = await userService.fetchSsoSettings(tenantUuid, undefined, abortController);
            if (ssoLoginDetails.loginUrl) {
                return { isConfigured: true, ssoLoginDetails };
            }
            const polledSsoLoginDetails = await pollSsoLoginUrl(tenantUuid, ssoLoginDetails.protocol, abortController);
            return polledSsoLoginDetails && { isConfigured: true, ssoLoginDetails: polledSsoLoginDetails };
        } catch (error) {
            if (!is404Error(error)) {
                throw error;
            }
            // The request fails when the tenant has no SSO configuration. Fetch is done again with both protocols (SAML and OIDC) to get the
            // SSO urls (plus service provider ID in SAML's case).
            const ssoLoginDetailsByProtocol = await fetchUnconfiguredSsoDetails(tenantUuid, abortController);
            return { isConfigured: false, ssoLoginDetailsByProtocol };
        }
    };

    const handleInvalidConfiguration = async (tenantUuid: string, abortController: AbortController) => {
        const ssoLoginDetailsByProtocol = await fetchUnconfiguredSsoDetails(tenantUuid, abortController);
        setIsPreviousConfigurationDeleted(false);
        setAlertModalState({
            title: t("Settings.ssoErrorTitle"),
            message: t("Settings.ssoInvalidConfigurationMessage"),
            isVisible: true,
        });
        setSsoState({ isConfigured: false, ssoLoginDetailsByProtocol });
    };

    React.useEffect(() => {
        if (!tenantDetails.uuid) {
            return;
        }
        const abortController = new AbortController();
        fetchSsoConfiguration(tenantDetails.uuid, abortController)
            .then((fetchedSsoState) => {
                if (abortController.signal.aborted) {
                    return;
                }
                return fetchedSsoState
                    ? setSsoState(fetchedSsoState)
                    : handleInvalidConfiguration(tenantDetails.uuid, abortController);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setAlertModalState({
                        title: t("Settings.errorTitle"),
                        message: t("Settings.ssoFetchErrorMessage"),
                        isVisible: true,
                    });
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsLoading(false);
                }
            });
        return () => abortController.abort();
    }, [tenantDetails.uuid]);

    const handleDelete = async () => {
        if (!tenantDetails.uuid) {
            return;
        }
        setIsConfirmDeleteModalVisible(false);
        setIsLoading(true);
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            await userService.deleteSsoSettings(tenantDetails.uuid, abortController);
            setIsPreviousConfigurationDeleted(true);
        } catch {
            if (!abortController.signal.aborted) {
                setAlertModalState({
                    title: t("Settings.ssoErrorTitle"),
                    message: t("Settings.ssoDeleteErrorMessage"),
                    isVisible: true,
                });
                setIsLoading(false);
            }
            return;
        }

        try {
            const ssoLoginDetailsByProtocol = await fetchUnconfiguredSsoDetails(tenantDetails.uuid, abortController);
            setSsoState({ isConfigured: false, ssoLoginDetailsByProtocol });
        } catch {
            if (!abortController.signal.aborted) {
                setSsoState(undefined);
                setAlertModalState({
                    title: t("Settings.ssoErrorTitle"),
                    message: t("Settings.ssoFetchErrorMessage"),
                    isVisible: true,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: SsoFormValues) => {
        if (!tenantDetails.uuid) {
            return;
        }

        setIsLoading(true);
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            await userService.createSsoConfiguration(
                tenantDetails.uuid,
                values.protocol,
                values.metadataUrl,
                values.clientId,
                values.clientSecret,
                values.oidcIssuer,
                abortController
            );
        } catch {
            if (!abortController.signal.aborted) {
                setAlertModalState({
                    title: t("Settings.ssoErrorTitle"),
                    message: t("Settings.ssoSaveErrorMessage"),
                    isVisible: true,
                });
                setIsLoading(false);
            }
            return;
        }

        try {
            const ssoLoginDetails = await pollSsoLoginUrl(tenantDetails.uuid, values.protocol, abortController);
            if (abortController.signal.aborted) {
                return;
            }
            if (!ssoLoginDetails) {
                await handleInvalidConfiguration(tenantDetails.uuid, abortController);
                return;
            }
            setSsoState({ isConfigured: true, ssoLoginDetails });
            showToast(t("Settings.ssoSaveMessage"), "success");
        } catch {
            if (!abortController.signal.aborted) {
                setAlertModalState({
                    title: t("Settings.ssoErrorTitle"),
                    message: t("Settings.ssoFetchErrorMessage"),
                    isVisible: true,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <SubPageLayout
                loading={isLoading}
                buttons={
                    ssoState?.isConfigured ? (
                        <Button
                            variant="Primary"
                            type="button"
                            onClick={() => setIsConfirmDeleteModalVisible(true)}
                            testId={testIds.workArea.settings.sso.changeProtocolButton}
                        >
                            {t("Settings.ssoConfigurations.changeProtocol")}
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="Primary"
                                type="submit"
                                form={CREATE_SSO_CONFIGURATION_FORM_ID}
                                disabled={!isDirty || (!isValid && submitCount > 0)}
                                testId={testIds.workArea.settings.sso.saveButton}
                            >
                                {t("Common.save")}
                            </Button>

                            <Button
                                variant="Secondary"
                                type="button"
                                onClick={() => setIsCancelModalVisible(true)}
                                disabled={!isDirty}
                                testId={testIds.workArea.settings.sso.cancelButton}
                            >
                                {t("Common.cancel")}
                            </Button>
                        </>
                    )
                }
                footerBanner={{ visible: !isValid && submitCount > 0, text: t("Common.fixErrors") }}
                hideBackArrow
                hideCancelButton
                padding="None"
                className={style.subPageLayout}
            >
                <Box columnLayout paddingTop="Sm" paddingBottom="Sm">
                    {ssoState &&
                        (ssoState.isConfigured ? (
                            <SingleSignOnDetails ssoLoginDetails={ssoState.ssoLoginDetails} />
                        ) : (
                            <SingleSignOnSettingsForm
                                key={formResetCount}
                                ssoLoginDetails={ssoState.ssoLoginDetailsByProtocol}
                                isPreviousConfigurationDeleted={isPreviousConfigurationDeleted}
                                setIsDirty={setIsDirty}
                                setIsValid={setIsValid}
                                setSubmitCount={setSubmitCount}
                                onSubmit={handleSubmit}
                            />
                        ))}
                </Box>
            </SubPageLayout>

            <DeleteModal
                title={t("Settings.ssoConfigurations.popUpTitle")}
                isOpen={isConfirmDeleteModalVisible}
                hideModal={() => setIsConfirmDeleteModalVisible(false)}
                textBundle={{
                    confirmationText: t("Common.delete").toUpperCase(),
                    deleteButtonText: t("Common.delete"),
                    cancelButtonText: t("Common.cancel"),
                }}
                onDelete={handleDelete}
                testIds={{
                    deleteButtonId: testIds.common.confirmationDialog.confirmButton,
                    cancelButtonId: testIds.common.confirmationDialog.closeButton,
                }}
            >
                <Box columnLayout>
                    <TextBlock>{t("Settings.ssoConfigurations.deleteDisclaimer")}</TextBlock>
                    <TextBlock disableBottomSpacing>{t("Settings.ssoConfigurations.confirmDelete")}</TextBlock>
                </Box>
            </DeleteModal>

            <Modal
                isOpen={isCancelModalVisible}
                hideModal={() => setIsCancelModalVisible(false)}
                title={t("Common.unsavedChanges.title")}
                buttons={
                    <>
                        <Button
                            variant="Primary"
                            onClick={() => setIsCancelModalVisible(false)}
                            testId={testIds.common.confirmationDialog.closeButton}
                        >
                            {t("Common.unsavedChanges.keepEditing")}
                        </Button>

                        <Button
                            variant="Secondary"
                            onClick={() => {
                                setFormResetCount(formResetCount + 1);
                                setIsCancelModalVisible(false);
                            }}
                            testId={testIds.common.confirmationDialog.confirmButton}
                        >
                            {t("Common.unsavedChanges.dontSave")}
                        </Button>
                    </>
                }
            >
                {t("Common.unsavedChanges.warning")}
            </Modal>

            <AlertModal
                title={alertModalState.title}
                variant={"Error"}
                buttons={
                    <Button
                        variant={"Primary"}
                        onClick={() =>
                            setAlertModalState({
                                title: t("Settings.errorTitle"),
                                message: t("Common.failureMessage"),
                                isVisible: false,
                            })
                        }
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
                isOpen={alertModalState.isVisible}
                hideModal={() =>
                    setAlertModalState({
                        title: t("Settings.errorTitle"),
                        message: t("Common.failureMessage"),
                        isVisible: false,
                    })
                }
            >
                {alertModalState.message}
            </AlertModal>
        </>
    );
};
